import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Rating,
  Chip,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import listingService from '../../services/listingService';
import { reservationService } from '../../services/reservationService';
import ReservationForm from '../../components/reservation/ReservationForm';
import PaymentForm from '../../components/reservation/PaymentForm';
import ReservationSummary from '../../components/reservation/ReservationSummary.js';

const steps = ['Détails', 'Paiement', 'Confirmation'];

const ReservationPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [listing, setListing] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [reservationData, setReservationData] = useState({
    startDate: null,
    endDate: null,
    guestCount: 1,
    specialRequests: '',
    totalAmount: 0,
    breakdown: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createdReservation, setCreatedReservation] = useState(null);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        setLoading(true);
        const response = await listingService.getListingById(listingId);
        setListing(response.data || response);
      } catch (err) {
        setError('Erreur lors du chargement de l\'annonce');
        console.error('Error fetching listing:', err);
      } finally {
        setLoading(false);
      }
    };

    if (listingId) {
      fetchListing();
    }
  }, [listingId]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReservationDataChange = (data) => {
    setReservationData(prev => ({
      ...prev,
      ...data
    }));
  };

  const handleCreateReservation = async (paymentData) => {
    try {
      setLoading(true);
      
      // Fonction pour formater les dates de manière sûre
      const formatDate = (date) => {
        if (!date) return null;
        if (typeof date === 'string') return date;
        if (date instanceof Date) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
        return null;
      };
      
      const reservationPayload = {
        listing_id: listingId,
        renter_id: user.id,
        start_date: formatDate(reservationData.startDate),
        end_date: formatDate(reservationData.endDate),
        guest_count: reservationData.guestCount,
        special_requests: reservationData.specialRequests,
        payment_method: paymentData.paymentMethod,
        payment_reference: paymentData.paymentReference,
        total_amount: reservationData.totalAmount
      };

      const response = await reservationService.createReservation(reservationPayload);
      setCreatedReservation(response.data);
      handleNext(); // Aller à l'étape de confirmation
    } catch (err) {
      setError('Erreur lors de la création de la réservation');
      console.error('Error creating reservation:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ReservationForm
            listing={listing}
            initialData={reservationData}
            onDataChange={handleReservationDataChange}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <PaymentForm
            reservationData={reservationData}
            listing={listing}
            onPayment={handleCreateReservation}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <ReservationSummary
            reservation={createdReservation}
            listing={listing}
            onFinish={() => navigate('/dashboard/reservations')}
          />
        );
      default:
        return 'Étape inconnue';
    }
  };

  if (loading && !listing) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Retour
        </Button>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Vous devez être connecté pour faire une réservation.
        </Alert>
        <Button
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
          variant="contained"
        >
          Se connecter
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header avec retour */}
      <Box sx={{ mb: 3 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" gutterBottom>
          Réserver cette annonce
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Colonne de gauche - Informations listing */}
        <Grid item xs={12} md={5}>
          <Card sx={{ position: 'sticky', top: 100 }}>
            {listing?.images?.[0] && (
              <CardMedia
                component="img"
                height="200"
                image={listing.images[0]}
                alt={listing.title}
              />
            )}
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {listing?.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: '1rem' }} />
                <Typography variant="body2" color="text.secondary">
                  {listing?.city}, {listing?.country}
                </Typography>
              </Box>

              {listing?.average_rating && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Rating
                    value={listing.average_rating}
                    readOnly
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    ({listing.review_count || 0} avis)
                  </Typography>
                </Box>
              )}

              <Typography variant="h5" color="primary" sx={{ mt: 2 }}>
                €{listing?.price}/jour
              </Typography>

              {listing?.features && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Caractéristiques:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {listing.features.slice(0, 4).map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Colonne de droite - Formulaire de réservation */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Contenu de l'étape */}
            <Box sx={{ minHeight: 400 }}>
              {getStepContent(activeStep)}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReservationPage;
